/**
 * Process-test diagnostics for the LSP — the in-editor half of P7.
 *
 * Runs the workspace's `test` declarations over their processes (engine
 * `runTests`, timeless — no coverage, no clock) and maps the outcomes to engine
 * `Diagnostic`s anchored at the offending assertion / pin:
 *   - a failing assertion → `error` (`test.assertion-fail`),
 *   - an OPEN / unprovable assertion → `warning` (`test.assertion-open`),
 *   - the engine's own enumeration diagnostics (`test.unmatched-pin`,
 *     `test.ambiguous-choice-key`, `test.trace-cap`, `test.no-target`, …) are
 *     forwarded verbatim, already carrying their pin/target source + severity.
 *
 * Merged into the package's diagnostic stream in `Workspace.recomputeFromState`,
 * gated behind the same coherent-model check as policies (a partial graph would
 * false-report). Findings route to the right file by `source.file` like any
 * other diagnostic.
 */

import {
  runTests,
  type Diagnostic,
  type ResolvedModel,
} from "@archlang/engine";

export function computeTestDiagnostics(cascaded: ResolvedModel): Diagnostic[] {
  const report = runTests(cascaded);
  const out: Diagnostic[] = [];
  for (const r of report.results) {
    for (const a of r.result?.perAssertion ?? []) {
      if (a.status === "pass") continue;
      const source = a.assertion.source;
      out.push({
        severity: a.status === "fail" ? "error" : "warning",
        message:
          a.status === "fail"
            ? `Test '${r.name}': assertion failed${a.detail ? ` — ${a.detail}` : ""}.`
            : `Test '${r.name}': assertion unprovable over the pinned trace-set${a.detail ? ` — ${a.detail}` : ""}.`,
        code: a.status === "fail" ? ("test.assertion-fail" as const) : ("test.assertion-open" as const),
        ...(source ? { source } : {}),
      });
    }
    // Engine enumeration diagnostics (unmatched/ambiguous pin, trace-cap,
    // no-target) already carry their own span + severity — forward as-is.
    out.push(...r.diagnostics);
  }
  return out;
}
